import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import ReportTable from "../../common/ReportTable/ReportTable";
import { useLoader } from "../../../context/LoaderContext";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import { useUserData } from "../../../hooks/useUserData";
import ConfirmModal from "../../common/ConfirmModal";
import {
  activateDepartment,
  fetchDepartmentLog,
  fetchDepartments,
  saveDepartment,
  softDeleteDepartment,
} from "../../../services/departmentService";
import "./DepartmentManagement.scss";

const FIELD_OPTIONS = [
  { key: "DepartmentName", label: "Department Name" },
  { key: "Status", label: "Status" },
  { key: "InUse", label: "In use" },
  { key: "SystemDateTime", label: "Last Updated" },
  { key: "UpdatedByName", label: "Updated By" },
];

const DEFAULT_COLUMNS = [
  "DepartmentName",
  "Status",
  "InUse",
  "SystemDateTime",
  "UpdatedByName",
];

const isRowUsed = (row) => row?.IsUsed === 1 || row?.IsUsed === true;

const getLogActionTone = (actionType = "") => {
  const action = String(actionType || "").trim().toLowerCase();
  if (action === "created") return "created";
  if (action === "updated") return "updated";
  if (action === "activated") return "activated";
  if (action === "archived" || action === "deleted") return "archived";
  return "default";
};

const DepartmentManagement = () => {
  const { userData } = useUserData();
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();

  const [rows, setRows] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [departmentName, setDepartmentName] = useState("");
  const [errors, setErrors] = useState({});
  const [logEntries, setLogEntries] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    row: null,
    action: "delete",
  });

  const layoutStorageKey = useMemo(
    () =>
      `dept-mgmt-layout-v3:${String(userData?.OrganizationId || "org").toLowerCase()}`,
    [userData?.OrganizationId],
  );

  const resetForm = () => {
    setEditingRow(null);
    setDepartmentName("");
    setErrors({});
    setLogEntries([]);
    setLogLoading(false);
  };

  const loadLogForDepartment = async (departmentId) => {
    if (!userData?.OrganizationId || !userData?.StaffId || !departmentId) {
      setLogEntries([]);
      return;
    }
    try {
      setLogLoading(true);
      const entries = await fetchDepartmentLog(userData, departmentId);
      setLogEntries(Array.isArray(entries) ? entries : []);
    } catch (error) {
      console.error("Error loading department log", error);
      setLogEntries([]);
    } finally {
      setLogLoading(false);
    }
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = async (row) => {
    setEditingRow(row);
    setDepartmentName(row.DepartmentName || "");
    setErrors({});
    setShowModal(true);
    await loadLogForDepartment(row.DepartmentId);
  };

  const closeFormModal = () => {
    setShowModal(false);
    resetForm();
  };

  const validate = () => {
    const next = {};
    const name = departmentName.trim();
    if (!name) next.departmentName = "Department name is required.";
    else if (name.length > 500)
      next.departmentName = "Maximum 500 characters allowed.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const loadDepartments = async () => {
    if (!userData?.OrganizationId || !userData?.StaffId) return;
    try {
      setListLoading(true);
      const list = await fetchDepartments(userData);
      setRows(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error("Error loading departments", error);
      showAlert({
        title: "Error",
        message: error?.message || "Failed to load departments.",
        type: "danger",
      });
      setRows([]);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    if (userData?.OrganizationId && userData?.StaffId) {
      loadDepartments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.OrganizationId, userData?.StaffId]);

  const handleSave = async () => {
    if (!validate() || !userData?.OrganizationId || !userData?.StaffId) return;

    try {
      showLoader(editingRow ? "Updating department..." : "Saving department...");
      const result = await saveDepartment(userData, {
        departmentId: editingRow?.DepartmentId || "",
        departmentName: departmentName.trim(),
      });
      showAlert({
        title: "Success",
        message: result.message,
        type: "success",
      });
      closeFormModal();
      await loadDepartments();
    } catch (error) {
      console.error("Error saving department", error);
      showAlert({
        title: "Error",
        message: error?.message || "Failed to save department.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const handleDeleteClick = (row, event) => {
    event?.stopPropagation?.();
    if (isRowUsed(row)) {
      showAlert({
        title: "Cannot delete",
        message:
          "This department is assigned to a user or study and cannot be deleted. Remove the assignment first.",
        type: "warning",
      });
      return;
    }
    setConfirmModal({ open: true, row, action: "delete" });
  };

  const handleActivateClick = (row, event) => {
    event?.stopPropagation?.();
    setConfirmModal({ open: true, row, action: "activate" });
  };

  const handleConfirmAction = async () => {
    const row = confirmModal.row;
    const action = confirmModal.action;
    if (!row || !userData?.OrganizationId || !userData?.StaffId) {
      setConfirmModal({ open: false, row: null, action: "delete" });
      return;
    }

    try {
      if (action === "activate") {
        showLoader("Activating department...");
        const result = await activateDepartment(userData, row.DepartmentId);
        showAlert({
          title: "Activated",
          message: result.message,
          type: "success",
        });
      } else {
        showLoader("Deleting department...");
        const result = await softDeleteDepartment(userData, row.DepartmentId);
        showAlert({
          title: "Deleted",
          message: result.message,
          type: "success",
        });
      }
      await loadDepartments();
    } catch (error) {
      console.error("Error updating department status", error);
      showAlert({
        title: "Error",
        message:
          error?.message ||
          (action === "activate"
            ? "Failed to activate department."
            : "Failed to delete department."),
        type: "danger",
      });
    } finally {
      hideLoader();
      setConfirmModal({ open: false, row: null, action: "delete" });
    }
  };

  const getFieldLabel = useCallback((key) => {
    const match = FIELD_OPTIONS.find((f) => f.key === key);
    return match?.label || key;
  }, []);

  const renderColumnSearch = useCallback((field, options) => {
    if (field !== "InUse") return undefined;
    if (options?.checkOnly) return true;
    return (
      <select
        className="report-table__column-search department-mgmt-column-select"
        value={options?.value || ""}
        disabled={options?.disabled}
        aria-label="Filter by in use"
        onChange={(event) => options?.onChange?.(event.target.value)}
      >
        <option value="">All</option>
        <option value="in-use">In use</option>
        <option value="not-in-use">Not in use</option>
      </select>
    );
  }, []);

  const matchColumnSearch = useCallback((row, field, searchValue) => {
    const term = String(searchValue || "").trim().toLowerCase();
    if (!term) return true;
    if (field === "InUse") {
      const used = isRowUsed(row);
      if (term === "in-use") return used;
      if (term === "not-in-use") return !used;
      return true;
    }
    return String(row[field] ?? "")
      .toLowerCase()
      .includes(term);
  }, []);

  const renderCell = useCallback(
    (row, field) => {
      if (field === "DepartmentName") {
        return (
          <button
            type="button"
            className="report-table__cell-link department-mgmt-name-link"
            title="Edit department"
            onClick={() => openEditModal(row)}
          >
            {row.DepartmentName || "—"}
          </button>
        );
      }
      if (field === "InUse") {
        const used = isRowUsed(row);
        return (
          <span
            className={`badge department-mgmt-inuse-badge ${
              used ? "is-used" : "is-free"
            }`}
          >
            {used ? "In use" : "Not in use"}
          </span>
        );
      }
      if (field === "Status") {
        const status = String(row.Status || "").toLowerCase();
        const active = status === "active";
        const inactive = status === "inactive" || status === "archived";
        const used = isRowUsed(row);
        const alreadyInactive =
          inactive || row?.IsArchived === 1 || row?.IsArchived === true;
        return (
          <div className="department-mgmt-status-cell">
            <span
              className={`badge ${active ? "bg-success" : "bg-secondary"}`}
            >
              {row.Status || "—"}
            </span>
            {alreadyInactive ? (
              <button
                type="button"
                className="btn btn-sm btn-outline-success department-mgmt-activate-btn"
                onClick={(event) => handleActivateClick(row, event)}
                title="Make active"
                aria-label={`Activate ${row.DepartmentName || "department"}`}
              >
                <i className="fas fa-undo" aria-hidden="true" />
              </button>
            ) : used ? null : (
              <button
                type="button"
                className="btn btn-sm btn-outline-danger department-mgmt-delete-btn"
                onClick={(event) => handleDeleteClick(row, event)}
                title="Make inactive"
                aria-label={`Deactivate ${row.DepartmentName || "department"}`}
              >
                <i className="fas fa-trash-alt" aria-hidden="true" />
              </button>
            )}
          </div>
        );
      }
      const value = row[field];
      return value == null || value === "" ? "—" : String(value);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userData?.OrganizationId, userData?.StaffId],
  );

  return (
    <div className="user-mgmt-list-page user-mgmt-page department-mgmt-page">
      <InfoAlert isOpen={alertState.isOpen} onClose={hideAlert} {...alertState} />
      <PageBreadcrumb
        items={[
          { label: "Home", path: "/notification-center" },
          { label: "Departments" },
        ]}
        actions={[
          {
            icon: "fas fa-refresh",
            label: "Refresh",
            onClick: loadDepartments,
          },
          {
            icon: "fas fa-plus",
            label: "Add Department",
            onClick: openAddModal,
          },
        ]}
      />

      <div className="department-mgmt-report-wrap">
        <ReportTable
          rows={rows}
          fieldOptions={FIELD_OPTIONS}
          defaultColumns={DEFAULT_COLUMNS}
          getFieldLabel={getFieldLabel}
          renderCell={renderCell}
          renderColumnSearch={renderColumnSearch}
          matchColumnSearch={matchColumnSearch}
          pageSizeOptions={[10, 25, 50, 100]}
          frozenColumnWidthRem={14}
          hideColumnHeaderActionsFields={["Status"]}
          persistLayoutKey={layoutStorageKey}
          loading={listLoading}
        />
      </div>

      <ConfirmModal
        isOpen={confirmModal.open}
        onClose={() =>
          setConfirmModal({ open: false, row: null, action: "delete" })
        }
        onConfirm={handleConfirmAction}
        title={
          confirmModal.action === "activate"
            ? "Activate department"
            : "Deactivate department"
        }
        message={
          confirmModal.row
            ? confirmModal.action === "activate"
              ? `Do you want to activate department "${confirmModal.row.DepartmentName}"?`
              : `Do you want to deactivate department "${confirmModal.row.DepartmentName}"?`
            : ""
        }
        confirmText="Yes"
        cancelText="No"
        confirmButtonClass={
          confirmModal.action === "activate" ? "btn-success" : "btn-danger"
        }
      />

      {showModal && (
        <div
          className="modal fade show d-block department-mgmt-modal-backdrop"
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered modal-xl department-mgmt-modal-dialog">
            <div className="modal-content department-mgmt-modal">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingRow ? "Update Department" : "Add Department"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={closeFormModal}
                />
              </div>

              <div className="department-mgmt-modal__body">
                <div className="department-mgmt-modal__form">
                  <div className="mb-3">
                    <label className="form-label">
                      Department Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control${errors.departmentName ? " is-invalid" : ""}`}
                      value={departmentName}
                      maxLength={500}
                      onChange={(e) => setDepartmentName(e.target.value)}
                      autoFocus
                    />
                    {errors.departmentName ? (
                      <div className="invalid-feedback d-block">
                        {errors.departmentName}
                      </div>
                    ) : null}
                  </div>
                  {editingRow ? (
                    <p className="department-mgmt-modal__hint mb-0">
                      Status:{" "}
                      <span
                        className={`department-mgmt-modal__status-chip${
                          String(editingRow.Status || "").toLowerCase() ===
                          "active"
                            ? " is-active"
                            : " is-inactive"
                        }`}
                      >
                        {editingRow.Status || "—"}
                      </span>
                    </p>
                  ) : (
                    <p className="department-mgmt-modal__hint text-muted mb-0">
                      Log appears after you save this department.
                    </p>
                  )}
                </div>

                <aside className="department-mgmt-modal__log" aria-label="Department log">
                  <div className="department-mgmt-modal__log-head">
                    <div className="department-mgmt-modal__log-head-main">
                      <i className="fas fa-history" aria-hidden="true" />
                      <strong>Activity log</strong>
                    </div>
                    {editingRow ? (
                      <span className="department-mgmt-modal__log-dept">
                        {editingRow.DepartmentName}
                      </span>
                    ) : null}
                  </div>
                  <div className="department-mgmt-modal__log-scroll">
                    {!editingRow ? (
                      <div className="department-mgmt-modal__log-empty">
                        <i className="fas fa-stream" aria-hidden="true" />
                        <p>Save the department first to start logging changes.</p>
                      </div>
                    ) : logLoading ? (
                      <div className="department-mgmt-modal__log-empty">
                        <p>Loading log…</p>
                      </div>
                    ) : logEntries.length === 0 ? (
                      <div className="department-mgmt-modal__log-empty">
                        <i className="fas fa-stream" aria-hidden="true" />
                        <p>No log entries yet.</p>
                      </div>
                    ) : (
                      <ul className="department-mgmt-modal__log-list">
                        {logEntries.map((entry) => {
                          const tone = getLogActionTone(entry.ActionType);
                          const detail =
                            entry.OldDepartmentName &&
                            entry.OldDepartmentName !== entry.DepartmentName
                              ? `${entry.OldDepartmentName} → ${entry.DepartmentName || "—"}`
                              : entry.DepartmentName || "—";
                          return (
                            <li
                              key={
                                entry.AuditLogId ||
                                `${entry.DepartmentId}-${entry.SystemDateTime}-${entry.ActionType}`
                              }
                              className="department-mgmt-modal__log-item"
                            >
                              <div className="department-mgmt-modal__log-meta">
                                <span
                                  className={`department-mgmt-modal__log-action is-${tone}`}
                                >
                                  {entry.ActionType || "—"}
                                </span>
                                <time className="department-mgmt-modal__log-time">
                                  {entry.SystemDateTime || "—"}
                                </time>
                              </div>
                              <div className="department-mgmt-modal__log-detail">
                                {detail}
                              </div>
                              <div className="department-mgmt-modal__log-by">
                                <i className="fas fa-user" aria-hidden="true" />
                                <span>{entry.ChangedByName || "—"}</span>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </aside>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={closeFormModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSave}
                >
                  {editingRow ? "Update" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartmentManagement;
